import React, { createContext, useReducer, useMemo, Dispatch } from 'react';

type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type NoticeType = {
  label: string;
  color: 'success' | 'error' | 'info';
};

export type FactoryType = {
  companyName: string;
  accountSiret: string;
  accountId: number;
};

export type RequestType = {
  quotationId: number;
};

type ContextPayload = {
  SET_NOTICE: NoticeType;
  SET_LOADING: boolean;
  SET_FACTORY: FactoryType;
  SET_REQUEST: RequestType;
};

export type ContextActions =
  ActionMap<ContextPayload>[keyof ActionMap<ContextPayload>];

type InitialStateType = {
  noticeData: NoticeType;
  reset: boolean;
  isLoading: boolean;
  factoryData: FactoryType;
  requestData: RequestType;
};

const initialState: InitialStateType = {
  noticeData: {
    label: '',
    color: 'success',
  },
  reset: false,
  isLoading: false,
  factoryData: {
    companyName: '',
    accountSiret: '',
    accountId: 0,
  },
  requestData: {
    quotationId: 0,
  },
};

const reducer = (
  state: InitialStateType,
  action: ContextActions,
): InitialStateType => {
  switch (action.type) {
    case 'SET_NOTICE':
      return {
        ...state,
        noticeData: action.payload,
        reset: !state.reset,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_FACTORY':
      return {
        ...state,
        factoryData: action.payload,
      };
    case 'SET_REQUEST':
      return {
        ...state,
        requestData: action.payload,
      };

    default:
      return state;
  }
};

export type MainContextType = {
  state: InitialStateType;
  dispatch: Dispatch<ContextActions>;
};

const MainContext = createContext<MainContextType>({
  state: initialState,
  dispatch: () => null,
});

type MainProviderType = {
  children: React.ReactNode;
};

const MainProvider: React.FC<MainProviderType> = ({
  children,
}: MainProviderType) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => {
    return { state, dispatch };
  }, [state]);

  return <MainContext.Provider value={value}>{children}</MainContext.Provider>;
};

export { MainProvider, MainContext };
